import type { GrammarRule } from './grammar'

/**
 * Italian grammar, from A1 to C2.
 *
 * Italian word order is kind to an English speaker; its morphology is not.
 * Articles change with the next sound, prepositions fuse with them, adjectives
 * and participles agree, and possessives carry an article except when they
 * don’t. So the early rules are mostly about form, with the endings that do the
 * work picked out in bold, and the later ones follow the congiuntivo from its
 * everyday uses to the literary ones.
 */
export const GRAMMAR_IT: GrammarRule[] = [
  // ------------------------------------------------------------------ A1
  {
    id: 'it-articles',
    level: 1,
    topic: 'nouns',
    title: 'The article changes with the word that follows',
    rule: 'Italian articles show gender and number, and the masculine ones also change with the sound that follows: il before most consonants, lo before s + consonant, z, gn and ps, and l’ before a vowel.',
    pattern: [{ word: ['il', 'lo', 'l’', 'la'] }, 'Noun'],
    examples: [
      { text: '[Il](il) [libro](Noun) è sul tavolo.', english: 'The book is on the table.' },
      { text: '[Lo](lo) [zaino](Noun) è pesante.', english: 'The backpack is heavy.' },
      { text: '[L’](l’)[amico](Noun) di Marco arriva domani.', english: 'Marco’s friend arrives tomorrow.' },
      { text: '[La](la) [casa](Noun) è grande.', english: 'The house is big.' },
    ],
    note: 'The plurals follow the same pattern: i libri, gli zaini, gli amici, le case.',
  },
  {
    id: 'it-dropped-pronouns',
    level: 1,
    topic: 'verbs',
    title: 'The verb ending says who, so the pronoun usually goes',
    rule: 'Italian verb endings show the subject, so io, tu and the rest are usually left out. Use them to contrast or stress who.',
    pattern: ['Verb ending'],
    examples: [
      { text: '[Parl**o**](Verb ending) inglese.', english: 'I speak English.' },
      { text: 'Dove [abit**i**](Verb ending)?', english: 'Where do you live?' },
      { text: '[Lavor**iamo**](Verb ending) a Milano.', english: 'We work in Milan.' },
    ],
    note: 'Add the pronoun for contrast: Io lavoro, lui no.',
  },
  {
    id: 'it-negation',
    level: 1,
    topic: 'negation',
    title: 'non before the verb — and double negatives are normal',
    rule: 'Put non straight before the verb. When niente, nessuno or mai comes after the verb, non stays in front as well.',
    pattern: [{ word: 'non' }, 'Verb', 'Negative word'],
    examples: [
      { text: '[Non](non) [capisco](Verb).', english: 'I don’t understand.' },
      { text: '[Non](non) [ho visto](Verb) [nessuno](Negative word).', english: 'I didn’t see anyone.' },
      { text: '[Non](non) [mangio](Verb) [mai](Negative word) carne.', english: 'I never eat meat.' },
    ],
    pitfall: {
      wrong: 'Ho visto nessuno.',
      right: 'Non ho visto nessuno.',
      why: 'With nessuno after the verb, non still has to come before it.',
    },
  },
  {
    id: 'it-adjectives',
    level: 1,
    topic: 'nouns',
    title: 'Adjectives follow the noun and agree with it',
    rule: 'Italian adjectives usually come after the noun and match it in gender and number. Adjectives ending in -e have one form for both genders.',
    pattern: ['Noun', 'Adjective'],
    examples: [
      { text: 'Ho una [macchina](Noun) [ross**a**](Adjective).', english: 'I have a red car.' },
      { text: 'Sono [libri](Noun) [interessant**i**](Adjective).', english: 'They’re interesting books.' },
      { text: 'È una [ragazza](Noun) [gentil**e**](Adjective).', english: 'She’s a nice girl.' },
    ],
    note: 'A few common adjectives usually go first — bello, buono, grande, piccolo — and bello changes like the article: un bel libro, un bello zaino.',
  },
  {
    id: 'it-articulated-prepositions',
    level: 1,
    topic: 'nouns',
    title: 'Prepositions merge with the article',
    rule: 'di, a, da, in and su join the definite article that follows them: a + il = al, su + il = sul, in + la = nella, da + la = dalla, di + il = del.',
    pattern: [{ word: ['al', 'sul', 'nella', 'dalla', 'del'] }],
    shape: 'contrast',
    examples: [
      { text: 'Vado [al](al) mercato.', english: 'I’m going to the market.' },
      { text: 'Le chiavi sono [sul](sul) tavolo.', english: 'The keys are on the table.' },
      { text: 'Abito [nella](nella) casa gialla.', english: 'I live in the yellow house.' },
      { text: 'Vengo [dalla](dalla) stazione.', english: 'I’m coming from the station.' },
      { text: 'È la macchina [del](del) professore.', english: 'It’s the teacher’s car.' },
    ],
    pitfall: {
      wrong: 'Il libro è su il tavolo.',
      right: 'Il libro è sul tavolo.',
      why: 'su and il always merge into sul.',
    },
  },
  {
    id: 'it-tu-lei',
    level: 1,
    topic: 'register',
    title: 'tu for friends, Lei for politeness',
    rule: 'tu is informal. Lei — often capitalized in writing — is the polite “you”, and takes the same verb form as “she”.',
    pattern: [{ word: 'tu' }, { word: 'Lei' }],
    shape: 'contrast',
    examples: [
      { text: 'E [tu](tu), come ti chiami?', english: 'And you, what’s your name?' },
      { text: 'E [Lei](Lei), come si chiama?', english: 'And you, what’s your name? (formal)' },
      { text: 'Scusi, [Lei](Lei) è di qui?', english: 'Excuse me, are you from around here?' },
    ],
    note: 'Scusa is informal and scusi polite: commands follow the same split.',
  },
  {
    id: 'it-piacere',
    level: 1,
    topic: 'verbs',
    title: 'piacere: the thing you like is the subject',
    rule: 'Mi piace means “it pleases me”. The thing liked is the subject, so the verb agrees with it — piace for one thing, piacciono for several.',
    pattern: ['Who likes it', 'Verb', 'What is liked'],
    examples: [
      { text: '[Mi](Who likes it) [piace](Verb) [la pizza](What is liked).', english: 'I like pizza.' },
      { text: '[Mi](Who likes it) [piacciono](Verb) [i gatti](What is liked).', english: 'I like cats.' },
      { text: '[Ti](Who likes it) [piace](Verb) [ballare](What is liked)?', english: 'Do you like dancing?' },
    ],
    pitfall: {
      wrong: 'Io piaccio la pizza.',
      right: 'Mi piace la pizza.',
      why: 'The pizza does the pleasing, so it is the subject.',
    },
  },

  // ------------------------------------------------------------------ A2
  {
    id: 'it-passato-prossimo',
    level: 2,
    topic: 'verbs',
    title: 'The past: avere or essere, then the participle',
    rule: 'The everyday past is a present form of avere or essere plus the past participle. essere goes with many verbs of coming, going and becoming — andare, venire, partire, arrivare, nascere, diventare — and with every reflexive verb, and then the participle agrees with the subject.',
    pattern: ['Auxiliary', 'Participle'],
    examples: [
      { text: '[Ho](Auxiliary) [mangiato](Participle) troppo.', english: 'I ate too much.' },
      { text: 'Maria [è](Auxiliary) [andat**a**](Participle) a Roma.', english: 'Maria went to Rome.' },
      { text: '[Siamo](Auxiliary) [arrivat**i**](Participle) tardi.', english: 'We arrived late.' },
    ],
    pitfall: {
      wrong: 'Ho andato al mare.',
      right: 'Sono andato al mare.',
      why: 'andare takes essere. Not every verb of motion does — camminare and viaggiare take avere — so learn the auxiliary with the verb.',
    },
  },
  {
    id: 'it-possessives',
    level: 2,
    topic: 'nouns',
    title: 'Possessives take the article — except with family',
    rule: 'Italian says la mia macchina, literally “the my car”. With a single family member the article drops — mia madre, tuo fratello — and in the plural it comes back: le mie sorelle.',
    pattern: ['Article', 'Possessive', 'Noun'],
    examples: [
      { text: '[La](Article) [mia](Possessive) [macchina](Noun) è vecchia.', english: 'My car is old.' },
      { text: '[Il](Article) [nostro](Possessive) [appartamento](Noun) è piccolo.', english: 'Our apartment is small.' },
      { text: '[Mia](Possessive) [madre](Noun) abita a Bologna.', english: 'My mother lives in Bologna.' },
      { text: '[Le](Article) [mie](Possessive) [sorelle](Noun) sono più grandi.', english: 'My sisters are older.' },
    ],
    pitfall: {
      wrong: 'La mia madre abita a Bologna.',
      right: 'Mia madre abita a Bologna.',
      why: 'A single family member takes no article.',
    },
    note: 'The exception has exceptions: loro always keeps the article — la loro madre — and so do affectionate forms like la mia mamma.',
  },
  {
    id: 'it-object-pronouns',
    level: 2,
    topic: 'verbs',
    title: 'Object pronouns: before the verb, or on the end of an infinitive',
    rule: 'lo, la, li, le, gli and the rest go before a conjugated verb, and attach to the end of an infinitive.',
    pattern: ['Pronoun', 'Verb'],
    shape: 'contrast',
    examples: [
      { text: '[Lo](Pronoun) [vedo](Verb) domani.', english: 'I’m seeing him tomorrow.' },
      { text: '[La](Pronoun) [conosci](Verb)?', english: 'Do you know her?' },
      { text: 'Voglio [veder**lo**](Pronoun) domani.', english: 'I want to see him tomorrow.' },
    ],
    pitfall: {
      wrong: 'Vedo lo domani.',
      right: 'Lo vedo domani.',
      why: 'With a conjugated verb, the pronoun comes first.',
    },
  },
  {
    id: 'it-reflexive',
    level: 2,
    topic: 'verbs',
    title: 'Reflexive verbs: mi alzo, ci siamo divertiti',
    rule: 'Many everyday verbs are reflexive — alzarsi, chiamarsi, divertirsi. The reflexive pronoun goes before the verb, and in the past they always take essere.',
    pattern: ['Reflexive pronoun', 'Verb'],
    examples: [
      { text: '[Mi](Reflexive pronoun) [alzo](Verb) alle sette.', english: 'I get up at seven.' },
      { text: 'Come [ti](Reflexive pronoun) [chiami](Verb)?', english: 'What’s your name?' },
      { text: '[Ci](Reflexive pronoun) [siamo divertit**i**](Verb) molto.', english: 'We had a great time.' },
    ],
    note: 'Plenty of verbs are reflexive in Italian and not in English: mi sveglio, “I wake up”.',
  },
  {
    id: 'it-stare-gerund',
    level: 2,
    topic: 'verbs',
    title: 'stare + gerund: happening right now',
    rule: 'A form of stare plus the gerund (-ando, -endo) describes something in progress at this moment.',
    pattern: ['Form of stare', 'Gerund'],
    examples: [
      { text: '[Sto](Form of stare) [mangi**ando**](Gerund).', english: 'I’m eating.' },
      { text: 'Cosa [stai](Form of stare) [fac**endo**](Gerund)?', english: 'What are you doing?' },
      { text: '[Stanno](Form of stare) [lavor**ando**](Gerund) in giardino.', english: 'They’re working in the garden.' },
    ],
    note: 'stare per plus an infinitive means “about to”: sto per uscire, “I’m about to go out”.',
  },

  // ------------------------------------------------------------------ B1
  {
    id: 'it-imperfetto-passato',
    level: 3,
    topic: 'verbs',
    title: 'Imperfetto for the background, passato prossimo for events',
    rule: 'The imperfetto describes how things were: states, habits, the scene. The passato prossimo tells what happened.',
    pattern: ['Imperfetto', 'Passato prossimo'],
    shape: 'contrast',
    examples: [
      {
        text: 'Quando [ero](Imperfetto) piccolo, [abitavo](Imperfetto) a Napoli.',
        english: 'When I was little, I lived in Naples.',
      },
      {
        text: '[Leggevo](Imperfetto) quando [ha squillato](Passato prossimo) il telefono.',
        english: 'I was reading when the phone rang.',
      },
      {
        text: '[Faceva](Imperfetto) bel tempo, così [siamo usciti](Passato prossimo).',
        english: 'The weather was nice, so we went out.',
      },
    ],
  },
  {
    id: 'it-ci-ne',
    level: 3,
    topic: 'nouns',
    title: 'ci for a place, ne for a quantity',
    rule: 'ci replaces a place, or a + something: ci vado, “I’m going there”. ne replaces di + something, and any quantity: ne ho due, “I have two of them”.',
    pattern: [{ word: ['ci', 'ne'] }, 'Verb'],
    examples: [
      {
        text: 'Vai a Roma? Sì, [ci](ci) [vado](Verb) domani.',
        english: 'Are you going to Rome? Yes, I’m going there tomorrow.',
      },
      { text: 'Quanti anni hai? [Ne](ne) [ho](Verb) trenta.', english: 'How old are you? I’m thirty.' },
      {
        text: 'Vuoi del caffè? No grazie, [ne](ne) [ho](Verb) già bevuto.',
        english: 'Want some coffee? No thanks, I’ve already had some.',
      },
    ],
    note: 'Where English can drop “of them”, Italian can’t: ne ho due, never ho due.',
  },
  {
    id: 'it-future-guess',
    level: 3,
    topic: 'verbs',
    title: 'The future for guessing: saranno le otto',
    rule: 'Italian uses the future tense to make a guess about the present: “it must be”, “I suppose”.',
    pattern: ['Future'],
    examples: [
      { text: 'Che ore sono? [Saranno](Future) le otto.', english: 'What time is it? It must be about eight.' },
      { text: 'Dove [sarà](Future) Marco?', english: 'Where could Marco be?' },
      { text: '[Avrà](Future) trent’anni.', english: 'He must be about thirty.' },
    ],
    note: 'The future perfect does the same for the past: sarà già partito, “he must have left already”.',
  },
  {
    id: 'it-congiuntivo',
    level: 3,
    topic: 'verbs',
    title: 'The congiuntivo after thinking, wanting and hoping',
    rule: 'After che, verbs of opinion, will, hope and emotion — penso che, voglio che, spero che, è importante che — take the congiuntivo.',
    pattern: ['Trigger', { word: 'che' }, 'Congiuntivo'],
    examples: [
      { text: '[Penso](Trigger) [che](che) [sia](Congiuntivo) vero.', english: 'I think it’s true.' },
      { text: '[Voglio](Trigger) [che](che) tu [venga](Congiuntivo).', english: 'I want you to come.' },
      { text: '[È importante](Trigger) [che](che) tu lo [sappia](Congiuntivo).', english: 'It’s important that you know.' },
    ],
    pitfall: {
      wrong: 'Voglio che tu vieni.',
      right: 'Voglio che tu venga.',
      why: 'volere che always takes the congiuntivo.',
    },
    note: 'In casual speech you will hear penso che è vero. In writing, and anywhere careful, use the congiuntivo.',
  },

  // ------------------------------------------------------------------ B2
  {
    id: 'it-periodo-ipotetico',
    level: 4,
    topic: 'verbs',
    title: 'se with the congiuntivo imperfetto, then the conditional',
    rule: 'For something unlikely or imaginary, the se half takes the congiuntivo imperfetto and the result takes the conditional.',
    pattern: [{ word: 'se' }, 'Congiuntivo imperfetto', 'Conditional'],
    examples: [
      {
        text: '[Se](se) [avessi](Congiuntivo imperfetto) tempo, [viaggerei](Conditional) di più.',
        english: 'If I had time, I’d travel more.',
      },
      {
        text: '[Se](se) [fossi](Congiuntivo imperfetto) in te, non lo [farei](Conditional).',
        english: 'If I were you, I wouldn’t do it.',
      },
      {
        text: 'Cosa [faresti](Conditional) [se](se) [vincessi](Congiuntivo imperfetto) alla lotteria?',
        english: 'What would you do if you won the lottery?',
      },
    ],
    pitfall: {
      wrong: 'Se avrei tempo, viaggerei di più.',
      right: 'Se avessi tempo, viaggerei di più.',
      why: 'The conditional never goes straight after se.',
    },
  },
  {
    id: 'it-si-impersonale',
    level: 4,
    topic: 'verbs',
    title: 'si: what people do in general',
    rule: 'si with a third-person verb makes a general statement — “one”, “people”, “you”. With a plural object, the verb goes plural.',
    pattern: [{ word: 'si' }, 'Verb'],
    examples: [
      { text: 'Qui [si](si) [mangia](Verb) bene.', english: 'You eat well here.' },
      { text: 'In Italia [si](si) [parla](Verb) italiano.', english: 'They speak Italian in Italy.' },
      { text: '[Si](si) [vendono](Verb) biciclette usate.', english: 'Used bikes for sale.' },
    ],
    note: 'Plural thing, plural verb: si vende una casa, si vendono case.',
  },
  {
    id: 'it-combined-pronouns',
    level: 4,
    topic: 'verbs',
    title: 'Two pronouns together: me lo, glielo',
    rule: 'When an indirect and a direct pronoun meet, the indirect one comes first and changes its vowel: mi becomes me, ti becomes te. gli and le both join lo to make one word, glielo.',
    pattern: ['Indirect', 'Direct', 'Verb'],
    examples: [
      { text: '[Me](Indirect) [lo](Direct) [dai](Verb)?', english: 'Will you give it to me?' },
      { text: '[Te](Indirect) [lo](Direct) [dico](Verb) dopo.', english: 'I’ll tell you later.' },
      { text: 'Glie**lo** [spiego](Verb) io.', english: 'I’ll explain it to him.' },
    ],
    pitfall: {
      wrong: 'Mi lo dai?',
      right: 'Me lo dai?',
      why: 'Before lo, mi changes to me.',
    },
  },

  // ------------------------------------------------------------------ C1
  {
    id: 'it-passato-remoto',
    level: 5,
    topic: 'register',
    title: 'The passato remoto: the past of books and history',
    rule: 'Written narrative and history use the passato remoto for completed events. In much of the north it is rare in speech; in parts of the south it is still used in conversation.',
    pattern: ['Passato remoto'],
    examples: [
      { text: 'Dante [nacque](Passato remoto) a Firenze nel 1265.', english: 'Dante was born in Florence in 1265.' },
      {
        text: '[Entrò](Passato remoto), [si sedette](Passato remoto) e [aspettò](Passato remoto).',
        english: 'He came in, sat down and waited.',
      },
      {
        text: 'I Romani [costruirono](Passato remoto) strade in tutto l’impero.',
        english: 'The Romans built roads across the empire.',
      },
    ],
  },
  {
    id: 'it-come-se',
    level: 5,
    topic: 'verbs',
    title: 'come se: as if',
    rule: 'come se always takes the congiuntivo — the imperfetto for something at the same time, the trapassato for something earlier.',
    pattern: [{ word: 'come se' }, 'Congiuntivo'],
    examples: [
      { text: 'Parla [come se](come se) [fosse](Congiuntivo) il capo.', english: 'He talks as if he were the boss.' },
      {
        text: 'Mi guardava [come se](come se) non mi [conoscesse](Congiuntivo).',
        english: 'She looked at me as if she didn’t know me.',
      },
      {
        text: 'Si comporta [come se](come se) non [fosse successo](Congiuntivo) niente.',
        english: 'He’s acting as if nothing had happened.',
      },
    ],
  },
  {
    id: 'it-benche',
    level: 5,
    topic: 'linking',
    title: 'benché and sebbene: although, with the congiuntivo',
    rule: 'benché, sebbene and nonostante all mean “although”, and all three take the congiuntivo.',
    pattern: [{ word: ['benché', 'sebbene', 'nonostante'] }, 'Congiuntivo'],
    examples: [
      { text: '[Benché](benché) [sia](Congiuntivo) stanco, continuo a lavorare.', english: 'Although I’m tired, I keep working.' },
      { text: '[Sebbene](sebbene) [piovesse](Congiuntivo), siamo usciti.', english: 'Although it was raining, we went out.' },
      {
        text: '[Nonostante](nonostante) [abbia](Congiuntivo) poco tempo, mi aiuta sempre.',
        english: 'Even though she has little time, she always helps me.',
      },
    ],
    note: 'anche se means the same and takes the ordinary indicative: anche se sono stanco.',
  },

  // ------------------------------------------------------------------ C2
  {
    id: 'it-superlative-congiuntivo',
    level: 6,
    topic: 'verbs',
    title: 'The congiuntivo after a superlative',
    rule: 'After a superlative, or words like l’unico and il primo, a relative clause takes the congiuntivo.',
    pattern: ['Superlative', { word: 'che' }, 'Congiuntivo'],
    examples: [
      {
        text: 'È [il libro più bello](Superlative) [che](che) io [abbia](Congiuntivo) mai letto.',
        english: 'It’s the most beautiful book I’ve ever read.',
      },
      {
        text: 'Sei [l’unica persona](Superlative) [che](che) mi [capisca](Congiuntivo).',
        english: 'You’re the only person who understands me.',
      },
    ],
  },
  {
    id: 'it-dislocation',
    level: 6,
    topic: 'order',
    title: 'The topic first, then a pronoun: la pizza, la mangio',
    rule: 'Spoken Italian often puts the topic at the front and repeats it as a pronoun: Il caffè lo prendo senza zucchero. It sounds natural and conversational, not wrong.',
    pattern: ['Topic', 'Pronoun', 'Verb'],
    examples: [
      {
        text: '[Il caffè](Topic) [lo](Pronoun) [prendo](Verb) senza zucchero.',
        english: 'I take my coffee without sugar.',
      },
      { text: '[Questo film](Topic) non [lo](Pronoun) [conosco](Verb).', english: 'I don’t know this movie.' },
      { text: '[Le chiavi](Topic), [le](Pronoun) [hai](Verb) prese tu?', english: 'The keys — did you take them?' },
    ],
    note: 'Without the pronoun, a fronted object sounds contrastive: Il caffè prendo, non il tè.',
  },
]
